import { useSelector, useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppointmentRequestForm from "./AppointmentRequestForm";
import { formatDate, fetchUserAppointments } from "../appointment/appointmentFunctions";
import Axios from "axios";
import { logout, setAuthToken } from "../common";
import { useNavigate } from "react-router-dom";
import PmsUrlConfig from "../../urlConfig";
import pmsConstants from "../../pmsConstants";

export default function SubmitAppointment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loginDetails = useSelector(state => state.loginDetails);
    const token = loginDetails?.jwtToken || "";
    const loggedInUser = loginDetails?.loggedInUser;

    const apptDetails = useSelector(state => state.appointmentDetails);
    const successMsg = apptDetails?.apptSuccessMsg;
    const errorMsg = apptDetails?.apptErrorMsg;

    const appointmentRequestSchema = Yup.object().shape({
        apptServiceType: Yup.string().required("Required"),
        apptReason: Yup.string().required("Required"),
        apptZipcode: Yup.string()
            .required("Required")
            .matches(/^[0-9]+$/, "Must be only digits")
            .length(5, "Must be exactly 5 digits"),
        searchRadius: Yup.string().required("Required").test('not-null', 'Required', (value) => {
            return value !== null && value !== undefined && value !== '';
        }),
        apptUrgency: Yup.string().required("Required"),
        deptFirstname: Yup.string().when("appointmentFor", {
            is: "dependent",
            then: Yup.string().required("Required!"),
        }),
        deptLastname: Yup.string().when("appointmentFor", {
            is: "dependent",
            then: Yup.string().required("Required!"),
        }),
        deptAge: Yup.string().when("appointmentFor", {
            is: "dependent",
            then: Yup.string().required("Required!"),
        }),
    });

    return (
        <Formik
            initialValues={{
                appointmentFor: "self",
                apptServiceType: "",
                apptReason: "",
                apptZipcode: "",
                searchRadius: "5",
                apptUrgency: pmsConstants.URGENCY_TYPE_URGENT_STRING,
                apptInsurance: "",
                deptFirstname: "",
                deptLastname: "",
                deptGender: "",
                deptAge: "",
            }}
            validationSchema={appointmentRequestSchema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                const dependentDetails =
                    values.appointmentFor === "dependent"
                        ? {
                            firstname: values.deptFirstname,
                            lastname: values.deptLastname,
                            gender: values.deptGender,
                            age: parseInt(values.deptAge),
                        }
                        : {};

                setAuthToken(token);

                Axios.post(PmsUrlConfig.BOS_SUBMIT_APPOINTMENT_REQUEST_URL, {
                    appointment_basic_details: {
                        service_type: values.apptServiceType,
                        reason: values.apptReason,
                        urgency_type: values.apptUrgency,
                        create_date_time: formatDate(new Date()),
                        insurance: values.apptInsurance,
                        appointment_for: values.appointmentFor,
                    },
                    created_by_user: { _id: loggedInUser._id },
                    zip_code: values.apptZipcode,
                    city: values.apptCity,
                    search_radius_miles: parseInt(values.searchRadius),
                    dependent_details: dependentDetails,
                })
                    .then((response) => {
                        if (response.status === 200) {
                            fetchUserAppointments(loggedInUser._id, 0, dispatch, token);
                            resetForm();
                            setSubmitting(false);
                            navigate(`${PmsUrlConfig.APPOINTMENTS}`);
                        } else if (response.status === 400) {
                            dispatch({
                                type: "apptSubmitError",
                                payload: response.data.error_code.description,
                            });
                            setSubmitting(false);
                        } else if (response.status === 401 || response.status === 403) {
                            logout();
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        setSubmitting(false);
                        dispatch({
                            type: "apptSubmitError",
                            payload:
                                error?.response?.data?.error_code?.description ||
                                "Internal Server Error!!",
                        });
                    });
            }}
        >
            {({
                errors,
                touched,
                handleSubmit,
                handleBlur,
                handleChange,
                isSubmitting,
                handleReset,
                setFieldValue,
                values,
            }) => (
                <Form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="sticky top-[70px] z-20 mb-2 space-y-2 bg-[#edeef2] px-4 sm:px-6">
                        {successMsg && (
                            <div className="p-3 text-green-800 bg-green-100 rounded shadow">
                                {successMsg}
                            </div>
                        )}
                        {errorMsg && (
                            <div className="p-3 text-red-800 bg-red-100 rounded shadow">
                                {errorMsg}
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-xl shadow-lg">
                        <AppointmentRequestForm
                            errors={errors}
                            touched={touched}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            dispatch={dispatch}
                        />

                        <div className="flex justify-end gap-3 py-6 px-4 sm:px-6 border-t border-gray-200">
                            {/* Reset Button */}
                            <button
                                type="button"
                                onClick={handleReset}
                                className="
                                    px-6 py-2 h-10
                                    bg-white text-gray-700 font-semibold
                                    border border-gray-300
                                    rounded-lg
                                    shadow-sm
                                    hover:bg-gray-100
                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400
                                    transition-all duration-300
                                "
                            >
                                Reset
                            </button>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="
                                    px-6 py-2 h-10
                                    bg-gradient-to-r from-[#0B6D76] to-[#09757F]
                                    text-white font-semibold
                                    rounded-lg
                                    shadow-lg
                                    hover:from-[#095C64] hover:to-[#0A6B73]
                                    focus:outline-none focus:ring-2 focus:ring-offset-4 focus:ring-[#0B6D76]
                                    transition-all duration-300
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                "
                            >
                                Submit Request
                            </button>
                        </div>

                    </div>

                </Form>
            )}
        </Formik>
    );
}
